/**
 * .slbz archive parser. Mirrors core/src/archive.hpp v2.1 exactly.
 *
 * Format (all integers little-endian)
 * ───────────────────────────────────
 * File header  (12 bytes)
 *   [0..3]   magic        'S','L','B','Z'
 *   [4]      version      0x01
 *   [5..7]   reserved     0x00 0x00 0x00
 *   [8..11]  file_count   uint32
 *
 * Entry (repeated file_count times, variable)
 *   [0..1]   name_len        uint16
 *   [2..N]   filename        UTF-8 (no null terminator)
 *   [..]     original_size   uint64
 *   [..]     compressed_size uint64
 *   [..]     timestamp       uint64  (Unix epoch seconds)
 *   [..]     crc32           uint32
 *   [..]     compressed_data (compressed_size bytes)
 */

export type SlbzField = {
  /** Stable id used to highlight ribbon segments and legend rows in sync. */
  id: string;
  /** Byte offset into the archive where the field starts. */
  offset: number;
  /** Field length in bytes. */
  length: number;
  /** Which conceptual section this field belongs to. */
  group: 'header' | 'entry' | 'data';
  /** Short label shown in the legend. */
  label: string;
  /** One-line plain-English description. */
  hint: string;
  /** Decoded human-readable value (e.g. "SLBZ", "1", "hello.txt", "462 B"). */
  display: string;
  /** Raw bytes for the ribbon to render as hex. May be truncated for huge fields. */
  bytes: Uint8Array;
  /** True if `bytes` is a truncated view of a larger field (data section). */
  truncated?: boolean;
};

export type SlbzAnatomy = {
  /** Total archive size in bytes. */
  totalSize: number;
  /** True if magic + version match what we know. */
  valid: boolean;
  /** Parser warnings (e.g. "version 2, expected 1"). */
  warnings: string[];
  /** All fields in source order. */
  fields: SlbzField[];
};

const MAGIC = [0x53, 0x4c, 0x42, 0x5a]; // "SLBZ"
const KNOWN_VERSION = 0x01;

/** Maximum bytes of compressed data we keep in the field's `bytes` view. */
const DATA_PEEK = 16;

export function parseSlbz(buf: Uint8Array): SlbzAnatomy {
  const fields: SlbzField[] = [];
  const warnings: string[] = [];
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);

  let cursor = 0;
  let valid = true;

  /* ── File header ───────────────────────────────────────────────── */
  if (buf.length < 12) {
    return {
      totalSize: buf.length,
      valid: false,
      warnings: ['Buffer is shorter than the 12-byte header.'],
      fields: [],
    };
  }

  const magicBytes = buf.slice(0, 4);
  const magicOk = MAGIC.every((b, i) => magicBytes[i] === b);
  if (!magicOk) {
    valid = false;
    warnings.push('Magic bytes are not "SLBZ" — this may not be a .slbz file.');
  }

  fields.push({
    id: 'magic',
    offset: 0,
    length: 4,
    group: 'header',
    label: 'Magic',
    hint: 'Every .slbz starts with these four ASCII bytes — the signature that says "this is one of mine".',
    display: '"SLBZ"',
    bytes: magicBytes,
  });

  const version = buf[4];
  if (version !== KNOWN_VERSION) {
    warnings.push(`Version ${version} is unknown (parser expects ${KNOWN_VERSION}).`);
  }
  fields.push({
    id: 'version',
    offset: 4,
    length: 1,
    group: 'header',
    label: 'Version',
    hint: 'Format version. Bump this when the on-disk layout changes incompatibly.',
    display: `0x${version.toString(16).padStart(2, '0')}`,
    bytes: buf.slice(4, 5),
  });

  fields.push({
    id: 'reserved',
    offset: 5,
    length: 3,
    group: 'header',
    label: 'Reserved',
    hint: 'Three padding bytes, kept zero. Reserved for future flags.',
    display: '0x00 · 0x00 · 0x00',
    bytes: buf.slice(5, 8),
  });

  const fileCount = view.getUint32(8, true);
  fields.push({
    id: 'count',
    offset: 8,
    length: 4,
    group: 'header',
    label: 'File count',
    hint: 'How many entries follow this header.',
    display: `${fileCount}`,
    bytes: buf.slice(8, 12),
  });

  cursor = 12;

  /* ── Entries ───────────────────────────────────────────────────── */
  // Cap the parser at the first entry for the anatomy view. Showing every
  // entry inflates the legend without adding insight; the format repeats.
  if (fileCount > 0) {
    if (cursor + 2 > buf.length) {
      warnings.push('Truncated before first entry name length.');
      return { totalSize: buf.length, valid: false, warnings, fields };
    }

    const nameLen = view.getUint16(cursor, true);
    const nameLenOff = cursor;
    fields.push({
      id: 'name_len',
      offset: nameLenOff,
      length: 2,
      group: 'entry',
      label: 'Name length',
      hint: 'Length in bytes of the UTF-8 filename that follows.',
      display: `${nameLen}`,
      bytes: buf.slice(nameLenOff, nameLenOff + 2),
    });
    cursor += 2;

    if (cursor + nameLen > buf.length) {
      warnings.push('Truncated mid-filename.');
      return { totalSize: buf.length, valid: false, warnings, fields };
    }

    const nameBytes = buf.slice(cursor, cursor + nameLen);
    const name = new TextDecoder().decode(nameBytes);
    fields.push({
      id: 'name',
      offset: cursor,
      length: nameLen,
      group: 'entry',
      label: 'Filename',
      hint: 'The original file name, UTF-8 encoded, no null terminator.',
      display: `"${name}"`,
      bytes: nameBytes,
    });
    cursor += nameLen;

    if (cursor + 28 > buf.length) {
      warnings.push('Truncated before entry metadata.');
      return { totalSize: buf.length, valid: false, warnings, fields };
    }

    const originalSize = Number(view.getBigUint64(cursor, true));
    fields.push({
      id: 'orig',
      offset: cursor,
      length: 8,
      group: 'entry',
      label: 'Original size',
      hint: 'Byte length of the source file, before compression.',
      display: humanBytes(originalSize),
      bytes: buf.slice(cursor, cursor + 8),
    });
    cursor += 8;

    const compressedSize = Number(view.getBigUint64(cursor, true));
    fields.push({
      id: 'comp',
      offset: cursor,
      length: 8,
      group: 'entry',
      label: 'Compressed size',
      hint: 'Byte length of the LZSS stream that follows the metadata.',
      display: humanBytes(compressedSize),
      bytes: buf.slice(cursor, cursor + 8),
    });
    cursor += 8;

    const timestamp = Number(view.getBigUint64(cursor, true));
    fields.push({
      id: 'ts',
      offset: cursor,
      length: 8,
      group: 'entry',
      label: 'Timestamp',
      hint: 'Modification time, Unix epoch seconds.',
      display: timestamp > 0 ? new Date(timestamp * 1000).toISOString().slice(0, 19).replace('T', ' ') : '(unset)',
      bytes: buf.slice(cursor, cursor + 8),
    });
    cursor += 8;

    const crc = view.getUint32(cursor, true);
    fields.push({
      id: 'crc',
      offset: cursor,
      length: 4,
      group: 'entry',
      label: 'CRC-32',
      hint: 'Checksum of the original (uncompressed) bytes. Verified on extract.',
      display: '0x' + crc.toString(16).padStart(8, '0'),
      bytes: buf.slice(cursor, cursor + 4),
    });
    cursor += 4;

    /* ── Compressed data ─────────────────────────────────────────── */
    const dataAvailable = Math.min(compressedSize, buf.length - cursor);
    const peek = buf.slice(cursor, cursor + Math.min(DATA_PEEK, dataAvailable));
    fields.push({
      id: 'data',
      offset: cursor,
      length: compressedSize,
      group: 'data',
      label: 'LZSS stream',
      hint: 'The compressed bytes themselves. Variable length, decoded by the LZSS engine.',
      display: humanBytes(compressedSize),
      bytes: peek,
      truncated: compressedSize > peek.length,
    });
    cursor += compressedSize;

    if (fileCount > 1) {
      warnings.push(
        `Archive contains ${fileCount} entries. Anatomy figure shows the first; the rest follow the same shape.`,
      );
    }
  }

  return {
    totalSize: buf.length,
    valid,
    warnings,
    fields,
  };
}

function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / 1024 / 1024).toFixed(2)} MiB`;
}
